import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Spinner } from "reactstrap";

/**
 * Resident list — Unit Number, Name, Contact Number, Email.
 *
 * People are boxed per unit (per tenancy window): lease holders first, then
 * occupants, each labelled and styled so the two are never mistaken for one
 * another. Name, Contact Number and Email are editable in place for CURRENT
 * residents. Leaving a changed cell opens a small confirm card showing the old
 * and new value; nothing is saved until Save is pressed there (Enter saves,
 * Escape or clicking elsewhere puts the old value back). Past residents come
 * from archived items and historic snapshots, which are records of what was
 * true at the time, so their contact is shown read-only. A vacant unit (from
 * the live mastersheet) is its own box with a Vacant marker.
 */
const cell = (value) => (String(value ?? "").trim() ? String(value).trim() : "—");

const rowKey = (row, i) => `${row.buildingName}-${row.unitNo}-${row.moveIn}-${i}`;

/** Current tenancies can be edited; edits save to the Resident Directory store. */
export const isRowEditable = (row) =>
  row?.status === "current" && (row?.source === "active" || row?.source === "directory");

/**
 * One box per unit per tenancy window. Occupants belong to a tenancy, so a
 * unit's past and current tenancies stay in separate boxes rather than being
 * mixed into one household that never existed.
 */
export const groupRowsByUnit = (rows) => {
  const groups = [];
  const index = new Map();
  (rows || []).forEach((row) => {
    const key =
      row.status === "current"
        ? [row.buildingName ?? "", row.unitNo, "current"].join("|")
        : [row.buildingName ?? "", row.unitNo, row.moveIn ?? "", row.moveOut ?? ""].join("|");
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ key, rows: [] });
    }
    groups[index.get(key)].rows.push(row);
  });
  for (const group of groups) {
    group.rows.sort((a, b) => {
      if (a.role !== b.role) return a.role === "occupant" ? 1 : -1;
      return String(a.name).localeCompare(String(b.name));
    });
  }
  return groups;
};

const show = (v) => (String(v ?? "").trim() ? String(v).trim() : "(blank)");

/**
 * The confirm step under an edited cell. Shows exactly what is about to change
 * and does nothing until Save. Enter saves, Escape cancels, clicking anywhere
 * outside the cell cancels.
 */
const ConfirmEdit = ({ label, from, to, onConfirm, onCancel, busy, duplicate, duplicateAccepted = false, onAcceptDuplicate, reason = "", onReasonChange }) => {
  const saveRef = useRef(null);
  useEffect(() => {
    saveRef.current?.focus();
  }, []);
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && (!duplicate || (duplicateAccepted && reason.trim().length >= 10))) {
      e.preventDefault();
      onConfirm();
    }
  };
  return (
    <div className="rd-confirm" role="dialog" aria-label="Save this change?" onKeyDown={onKeyDown}>
      <div className="rd-confirm-title">Save this change?</div>
      <div className="rd-confirm-diff">
        <span className="rd-confirm-label">{label}</span>
        <span className="rd-confirm-from">{show(from)}</span>
        <span className="rd-confirm-arrow" aria-hidden="true">→</span>
        <span className="rd-confirm-to">{show(to)}</span>
      </div>
      {duplicate && (
        <div className="rd-duplicate-warning" role="alert">
          <div className="rd-duplicate-title">
            <i className="mdi mdi-alert-outline" aria-hidden="true" /> Contact already in use
          </div>
          <p>
            <strong>{duplicate.value}</strong> is already assigned to lease holder <strong>{duplicate.residentName}</strong> in this unit.
          </p>
          {duplicateAccepted && (
            <label className="rd-duplicate-reason">
              Reason for sharing this contact information
              <textarea
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                placeholder="For example: Both lease holders use a shared family email account."
                maxLength={300}
                autoFocus
              />
              <span>{reason.trim().length < 10 ? "Enter at least 10 characters." : `${reason.trim().length}/300`}</span>
            </label>
          )}
        </div>
      )}
      <div className="rd-confirm-actions">
        <button type="button" className="rd-confirm-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {duplicate && !duplicateAccepted ? (
          <button type="button" ref={saveRef} className="rd-confirm-btn rd-confirm-btn-warning" onClick={onAcceptDuplicate} disabled={busy}>
            Use anyway
          </button>
        ) : (
          <button
            type="button"
            ref={saveRef}
            className="rd-confirm-btn rd-confirm-btn-primary"
            onClick={onConfirm}
            disabled={busy || (duplicate && reason.trim().length < 10)}
          >
            {busy ? <Spinner size="sm" /> : duplicate ? "Save exception" : "Save"}
          </button>
        )}
      </div>
    </div>
  );
};

/** Cancels a pending confirm when the pointer goes down outside the cell. */
const useOutsideCancel = (ref, active, onCancel) => {
  useEffect(() => {
    if (!active) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onCancel();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [ref, active, onCancel]);
};

const EditableCell = ({ row, field, type, placeholder, label, onSave }) => {
  const original = row[field] ?? "";
  const [value, setValue] = useState(original);
  const [state, setState] = useState("idle"); // idle | confirm | saving | saved | error
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const savedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValue(row[field] ?? "");
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const revert = () => {
    setValue(original ?? "");
    setState("idle");
  };
  useOutsideCancel(wrapRef, state === "confirm", revert);

  const askToSave = () => {
    const next = String(value ?? "").trim();
    if (next === String(original ?? "").trim()) return;
    setState("confirm");
  };

  const confirm = async () => {
    const next = String(value ?? "").trim();
    setState("saving");
    try {
      await onSave(next);
      setState("saved");
      savedTimerRef.current = setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setState("error");
      setValue(original ?? ""); // put the old value back so nothing looks saved
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      revert();
      inputRef.current?.blur();
    }
  };

  const border =
    state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : state === "confirm" ? "#0e578e" : "transparent";

  return (
    <span className="rd-edit" ref={wrapRef}>
      <input
        ref={inputRef}
        type={type}
        className="rd-edit-input"
        style={{ borderColor: border }}
        value={value}
        placeholder={placeholder}
        aria-label={`${label} for ${row.name || `unit ${row.unitNo}`}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (state !== "confirm") askToSave();
        }}
        onKeyDown={onKeyDown}
        disabled={state === "saving"}
      />
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the Resident Directory" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
      {state === "confirm" && (
        <ConfirmEdit label={label} from={original} to={String(value ?? "").trim()} onConfirm={confirm} onCancel={revert} busy={false} />
      )}
    </span>
  );
};

/**
 * Contact rules, mirrored from the server (resident-directory.ts). Numbers: digits only, at
 * least ten, two per person. Emails: real addresses, two per person. Both are stored joined
 * as "first; second" (older records used " / "; both split).
 */
export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15;
export const MAX_PHONES = 2;
export const MAX_EMAILS = 2;
export const CONTACT_JOINER = "; ";
export const PHONE_JOINER = CONTACT_JOINER;
export const splitContactValues = (value) =>
  String(value ?? "")
    .split(/\s*[;,/]\s*|\s{2,}/g)
    .map((p) => p.trim())
    .filter(Boolean);
export const splitPhones = splitContactValues;
const normalizeContact = (field, value) =>
  field === "email" ? String(value).trim().toLowerCase() : String(value).replace(/\D/g, "");

/** Return the other lease holder sharing any proposed value in the same current unit. */
export const findDuplicateLeaseHolderContact = (rows, row, field, proposedValue) => {
  if (!row || row.role === "occupant") return null;
  const proposed = new Set(splitContactValues(proposedValue).map((value) => normalizeContact(field, value)).filter(Boolean));
  if (!proposed.size) return null;
  const other = (rows || []).find((candidate) => {
    if (!candidate || candidate === row || candidate.role === "occupant" || candidate.status !== "current") return false;
    if (candidate.unitNo !== row.unitNo || (candidate.buildingName || "") !== (row.buildingName || "")) return false;
    if (candidate.name === row.name && candidate.moveIn === row.moveIn) return false;
    return splitContactValues(candidate[field]).some((value) => proposed.has(normalizeContact(field, value)));
  });
  if (!other) return null;
  const value = splitContactValues(other[field]).find((candidate) => proposed.has(normalizeContact(field, candidate)));
  return { residentName: other.name || "another lease holder", value };
};
/** Why a typed number cannot be saved yet, or null. Blank is allowed (it clears). */
export const phoneProblem = (digits) =>
  digits && digits.length < PHONE_MIN_DIGITS ? `At least ${PHONE_MIN_DIGITS} digits` : null;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const emailProblem = (value) => (value && !EMAIL_RX.test(value) ? "Enter a valid email" : null);

const CONTACT_KINDS = {
  phone: {
    max: MAX_PHONES,
    type: "tel",
    inputMode: "numeric",
    pattern: "[0-9]*",
    maxLength: PHONE_MAX_DIGITS,
    sanitize: (raw) => raw.replace(/\D+/g, "").slice(0, PHONE_MAX_DIGITS),
    problem: phoneProblem,
    labels: ["Contact number", "Second contact number"],
    placeholders: ["Add number", "Second number"],
    addLabel: "Add a second number",
    removeLabel: "Remove second number",
    confirmLabel: "Contact number",
  },
  email: {
    max: MAX_EMAILS,
    type: "email",
    inputMode: "email",
    pattern: undefined,
    maxLength: 120,
    sanitize: (raw) => raw.replace(/\s+/g, "").slice(0, 120),
    problem: emailProblem,
    labels: ["Email", "Second email"],
    placeholders: ["Add email", "Second email"],
    addLabel: "Add a second email",
    removeLabel: "Remove second email",
    confirmLabel: "Email",
  },
};

/**
 * One or two values of a kind (numbers or emails) for a person. Typing is kept to what the
 * kind allows; a value that is not acceptable yet is flagged once the field is left and is
 * never saved. "+" opens a second line, "−" removes it. Leaving a changed field (or removing a
 * line) opens the confirm card; the save happens from its Save button.
 */
const MultiValueCell = ({ row, field, kind, onSave, findDuplicate }) => {
  const cfg = CONTACT_KINDS[kind];
  const original = row[field] ?? "";
  const fromRow = () => {
    const list = splitContactValues(original).slice(0, cfg.max);
    return list.length ? list : [""];
  };
  const [values, setValues] = useState(fromRow);
  const [touched, setTouched] = useState([]);
  const [state, setState] = useState("idle"); // idle | confirm | saving | saved | error
  const [pending, setPending] = useState(null); // { next, list }
  const [duplicateAccepted, setDuplicateAccepted] = useState(false);
  const [duplicateReason, setDuplicateReason] = useState("");
  const refs = useRef([]);
  const wrapRef = useRef(null);
  const savedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValues(fromRow());
    setTouched([]);
    setPending(null);
    setDuplicateAccepted(false);
    setDuplicateReason("");
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const joined = (list) => list.map((v) => v.trim()).filter(Boolean).join(CONTACT_JOINER);
  const problems = values.map(cfg.problem);
  const who = row.name || `unit ${row.unitNo}`;
  const savedValue = joined(splitContactValues(original));

  const revert = () => {
    setValues(fromRow());
    setTouched([]);
    setPending(null);
    setDuplicateAccepted(false);
    setDuplicateReason("");
    setState("idle");
  };
  useOutsideCancel(wrapRef, state === "confirm", revert);

  const askToSave = (list = values) => {
    if (list.some(cfg.problem)) return; // nothing half-typed is ever saved
    // An emptied second line simply goes away.
    const tidy = list.length === 2 && !list[1].trim() ? [list[0]] : list;
    if (tidy.length !== list.length) setValues(tidy);
    const next = joined(tidy);
    if (next === savedValue) return;
    setPending({ next, list: tidy, duplicate: findDuplicate?.(next) || null });
    setDuplicateAccepted(false);
    setDuplicateReason("");
    setState("confirm");
  };

  const confirm = async () => {
    if (!pending) return;
    setState("saving");
    try {
      if (pending.duplicate) {
        await onSave(pending.next, { duplicate_override_reason: duplicateReason.trim() });
      } else {
        await onSave(pending.next);
      }
      setPending(null);
      setDuplicateAccepted(false);
      setDuplicateReason("");
      setState("saved");
      savedTimerRef.current = setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      const response = err?.response?.data;
      if (response?.code === "DUPLICATE_LEASE_HOLDER_CONTACT" && response.duplicate) {
        setPending((current) => current ? { ...current, duplicate: response.duplicate } : current);
        setDuplicateAccepted(false);
        setDuplicateReason("");
        setState("confirm");
        return;
      }
      setState("error");
      setPending(null);
      setValues(fromRow()); // put the old value back so nothing looks saved
    }
  };

  const setAt = (i, raw) => setValues((prev) => prev.map((v, idx) => (idx === i ? cfg.sanitize(raw) : v)));
  const markTouched = (i) =>
    setTouched((t) => {
      const next = [...t];
      next[i] = true;
      return next;
    });
  const add = () => {
    if (values.length >= cfg.max) return;
    setValues((prev) => [...prev, ""]);
    setTimeout(() => refs.current[1]?.focus(), 0);
  };
  const remove = (i) => {
    const list = values.filter((_, idx) => idx !== i);
    const next = list.length ? list : [""];
    setValues(next);
    setTouched([]);
    askToSave(next);
  };
  const onKeyDown = (i) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      refs.current[i]?.blur();
    }
    if (e.key === "Escape") {
      revert();
      refs.current[i]?.blur();
    }
  };

  const border =
    state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : state === "confirm" ? "#0e578e" : "transparent";

  return (
    <span className="rd-edit rd-multi" ref={wrapRef}>
      {values.map((v, i) => {
        const problem = touched[i] ? problems[i] : null;
        return (
          <span className="rd-multi-line" key={i}>
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              type={cfg.type}
              inputMode={cfg.inputMode}
              pattern={cfg.pattern}
              maxLength={cfg.maxLength}
              className="rd-edit-input"
              style={{ borderColor: problem ? "#DC2626" : border }}
              value={v}
              placeholder={cfg.placeholders[i] || cfg.placeholders[1]}
              aria-label={`${cfg.labels[i] || cfg.labels[1]} for ${who}`}
              aria-invalid={!!problem}
              title={problem || undefined}
              onChange={(e) => setAt(i, e.target.value)}
              onBlur={() => {
                markTouched(i);
                if (state !== "confirm") askToSave();
              }}
              onKeyDown={onKeyDown(i)}
              disabled={state === "saving"}
            />
            {i === 0 && values.length < cfg.max ? (
              <button
                type="button"
                className="rd-multi-btn"
                aria-label={`${cfg.addLabel} for ${who}`}
                title={cfg.addLabel}
                onClick={add}
                disabled={state === "saving"}
              >
                +
              </button>
            ) : null}
            {i > 0 ? (
              <button
                type="button"
                className="rd-multi-btn"
                aria-label={`${cfg.removeLabel} for ${who}`}
                title="Remove"
                onClick={() => remove(i)}
                disabled={state === "saving"}
              >
                −
              </button>
            ) : null}
            {problem ? <span className="rd-multi-hint">{problem}</span> : null}
          </span>
        );
      })}
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the Resident Directory" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
      {state === "confirm" && pending && (
        <ConfirmEdit
          label={cfg.confirmLabel}
          from={savedValue}
          to={pending.next}
          onConfirm={confirm}
          onCancel={revert}
          busy={false}
          duplicate={pending.duplicate}
          duplicateAccepted={duplicateAccepted}
          onAcceptDuplicate={() => setDuplicateAccepted(true)}
          reason={duplicateReason}
          onReasonChange={setDuplicateReason}
        />
      )}
    </span>
  );
};

const PhoneCell = ({ row, onSave, findDuplicate }) => <MultiValueCell row={row} field="phone" kind="phone" onSave={onSave} findDuplicate={findDuplicate} />;
const EmailCell = ({ row, onSave, findDuplicate }) => <MultiValueCell row={row} field="email" kind="email" onSave={onSave} findDuplicate={findDuplicate} />;

/** Read-only values, one per line, or a dash. */
const MultiText = ({ value, kind }) => {
  const list = splitContactValues(value);
  if (!list.length) return "—";
  return (
    <span className="rd-multi-text">
      {list.map((v, i) =>
        kind === "email" ? (
          <a key={i} href={`mailto:${v}`} className="rd-contact-link">
            {v}
          </a>
        ) : kind === "phone" ? (
          <a key={i} href={`tel:${v}`} className="rd-contact-link">
            {v}
          </a>
        ) : (
          <span key={i}>{v}</span>
        )
      )}
    </span>
  );
};

const ResidentTable = ({ rows, onSaveContact, onSaveUnit, defaultExpanded = false }) => {
  const groups = useMemo(() => groupRowsByUnit(rows), [rows]);
  const [expanded, setExpanded] = useState(() => new Set(defaultExpanded ? groups.map(g => g.key) : []));
  const toggle = (key) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  return (
    <div className="rd-unit-directory">
      <div className="rd-unit-tools">
        <span>Open a unit to view and edit its residents.</span>
        <div>
          <button type="button" className="rd-disclosure-action" onClick={() => setExpanded(new Set(groups.map(g => g.key)))}>Expand all</button>
          <button type="button" className="rd-disclosure-action" onClick={() => setExpanded(new Set())}>Collapse all</button>
        </div>
      </div>
      {groups.map(group => {
        const first = group.rows[0];
        const people = group.rows.filter(row => !row.vacant);
        const holders = people.filter(row => row.role !== 'occupant').length;
        const occupants = people.length - holders;
        const missing = people.filter(row => !splitContactValues(row.email).length).length;
        const opened = expanded.has(group.key);
        const panelId = `rd-unit-${encodeURIComponent(group.key)}`;
        return (
          <section key={group.key} className={`rd-unit-accordion${opened ? ' is-open' : ''}${!people.length ? ' is-vacant' : ''}`}>
            <h2 className="rd-unit-heading">
              <button type="button" className="rd-unit-toggle" aria-expanded={opened} aria-controls={panelId} onClick={() => toggle(group.key)}>
                <span className="rd-unit-identity">
                  <span className="rd-unit-caption">Unit</span>
                  <span className="rd-unit-chip">{cell(first.unitNo)}</span>
                </span>
                <span className="rd-unit-overview">
                  <span className="rd-unit-names">{people.length ? `Residents: ${people.map(row => row.name).join(', ')}` : 'Ready for the next resident'}</span>
                  <span className="rd-unit-count">{first.status === 'past' ? 'Past tenancy' : people.length ? `${people.length} resident${people.length === 1 ? '' : 's'}` : 'Vacant unit'}{first.buildingName ? ` | ${first.buildingName}` : ''}</span>
                </span>
                <span className="rd-unit-tags">
                  {holders > 0 && <span className="rd-role-pill is-tenant">{holders} lease holder{holders === 1 ? '' : 's'}</span>}
                  {occupants > 0 && <span className="rd-role-pill is-occupant">{occupants} occupant{occupants === 1 ? '' : 's'}</span>}
                  {missing > 0 && <span className="rd-contact-warning">{missing} without email</span>}
                  {!people.length && <span className="rd-vacant-pill">Vacant</span>}
                </span>
                <i className={`mdi mdi-chevron-${opened ? 'up' : 'down'} rd-unit-chevron`} aria-hidden="true" />
              </button>
            </h2>
            <div id={panelId} className="rd-unit-panel" hidden={!opened}>
              {!people.length ? <p className="rd-vacant-note">No current residents are recorded for this unit.</p> : (
                <Table responsive className="rd-table rd-accordion-table mb-0">
                  <thead><tr><th scope="col">Resident</th><th scope="col">Contact number</th><th scope="col">Email addresses</th></tr></thead>
                  <tbody>
                    {people.map((row, i) => {
                      const editable = isRowEditable(row) && typeof onSaveContact === 'function';
                      const nameEditable = editable && typeof onSaveUnit === 'function';
                      const isOccupant = row.role === 'occupant';
                      return (
                        <tr key={rowKey(row, i)} className={isOccupant ? 'rd-row-occupant' : 'rd-row-tenant'}>
                          <td data-label="Resident">
                            <span className="rd-person">
                              {nameEditable ? (
                                <EditableCell row={row} field="name" type="text" placeholder="Name" label="Resident name"
                                  onSave={value => onSaveUnit(row, { name_edit: { from: row.name, to: value, role: row.role } })} />
                              ) : <span className="rd-person-name">{cell(row.name)}</span>}
                              <span className={`rd-role-pill ${isOccupant ? 'is-occupant' : 'is-tenant'}`}>{isOccupant ? 'Occupant' : 'Lease Holder'}</span>
                            </span>
                          </td>
                          <td data-label="Contact number">{editable ? <PhoneCell row={row} findDuplicate={value => findDuplicateLeaseHolderContact(people, row, "phone", value)} onSave={(value, options) => options ? onSaveContact(row, { phone: value }, options) : onSaveContact(row, { phone: value })} /> : <MultiText value={row.phone} kind="phone" />}</td>
                          <td data-label="Email addresses">{editable ? <EmailCell row={row} findDuplicate={value => findDuplicateLeaseHolderContact(people, row, "email", value)} onSave={(value, options) => options ? onSaveContact(row, { email: value }, options) : onSaveContact(row, { email: value })} /> : <MultiText value={row.email} kind="email" />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export { ResidentTable };
